/**
 * #179 — "3D flythrough" trigger button for incident/community detail panels.
 *
 * Operator-triggered camera tour: descends and orbits the site over
 * photorealistic 3D context (streamed on demand, released when the tour ends),
 * then restores the prior view. The same button stops a running flythrough.
 * Styled to match the #178 viewshed toggle (the sibling 3D affordance).
 */
import type { FlythroughTarget } from './flythroughPath';

export function FlythroughButton({
  target,
  active,
  onFlythrough,
}: {
  target: FlythroughTarget;
  /** Whether a flythrough is currently running (drives label + stop action). */
  active: boolean;
  onFlythrough: (target: FlythroughTarget) => void;
}): JSX.Element {
  return (
    <div className="space-y-1">
      <button
        type="button"
        data-testid="flythrough-btn"
        aria-pressed={active}
        onClick={() => onFlythrough(target)}
        className={`block w-full text-center text-sm py-1.5 rounded border ${
          active
            ? 'border-sky-500 bg-sky-700/50 text-sky-100 hover:bg-sky-700'
            : 'border-slate-700 bg-slate-800 text-sky-300 hover:bg-slate-700'
        }`}
      >
        {active ? '⏹ End 3D flythrough' : '✈ 3D flythrough'}
      </button>
      {active && (
        <p className="text-[10px] leading-tight text-slate-500">
          Orbiting the site in 3D — photorealistic tiles stream on demand and are
          released when the tour ends. Your previous view is restored automatically.
        </p>
      )}
    </div>
  );
}
